import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { ApiError, ApiResponse } from './api.types';

class ApiClient {
  private client: AxiosInstance;
  private refreshPromise: Promise<void> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private async getAuthService() {
    try {
      const { authService } = await import('../../features/authentication/store/authStore');
      return authService;
    } catch (error) {
      console.error('Failed to import auth service:', error);
      return null;
    }
  }

  /**
   * Refresh token with deduplication
   * Multiple simultaneous calls will share the same refresh promise
   */
  private async refreshTokenIfNeeded(): Promise<string | null> {
    // If already refreshing, return the existing promise
    if (this.refreshPromise) {
      await this.refreshPromise;
      const authService = await this.getAuthService();
      const tokens = authService?.getCurrentTokens();
      return tokens?.idToken || null;
    }

    // Create new refresh promise
    this.refreshPromise = (async () => {
      try {
        const authService = await this.getAuthService();
        if (!authService) {
          throw new Error('Auth service not available');
        }

        await authService.refreshSession();
      } finally {
        // Clear the promise after completion
        this.refreshPromise = null;
      }
    })();

    await this.refreshPromise;
    const authService = await this.getAuthService();
    const tokens = authService?.getCurrentTokens();
    return tokens?.idToken || null;
  }

  private setupInterceptors() {
    // Request interceptor to add Cognito auth token and subject context
    this.client.interceptors.request.use(
      async (config) => {
        try {
          const authService = await this.getAuthService();
          if (!authService) {
            return config;
          }

          // Check if user is authenticated
          const isAuthenticated = await authService.isAuthenticated();

          if (isAuthenticated) {
            // Check if token needs refresh (expires in <10 minutes)
            if (authService.isSessionNearExpiry()) {
              const token = await this.refreshTokenIfNeeded();
              if (token) {
                config.headers.Authorization = `Bearer ${token}`;
              }
            } else {
              // Use current token
              const currentTokens = authService.getCurrentTokens();
              if (currentTokens?.idToken) {
                config.headers.Authorization = `Bearer ${currentTokens.idToken}`;
              }
            }
          }

          // Add all active context headers (tenant, domain, subject) if available
          try {
            const { getContextHeaders } = await import('../../stores/contextStore');
            const contextHeaders = getContextHeaders();

            // Add all available context headers
            Object.entries(contextHeaders).forEach(([key, value]) => {
              if (value) {
                config.headers[key] = value;
              }
            });
          } catch (error) {
            // Silently ignore if context store is not available
          }
        } catch (error) {
          // Silently ignore if no valid Cognito session
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response: AxiosResponse): AxiosResponse => {
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config;

        // Special handling for /auth/me endpoint failures
        if (originalRequest?.url === '/auth/me') {
          const status = error.response?.status;

          // For /auth/me, authentication errors indicate invalid session - auto-logout
          if (status === 401 || status === 403 || status === 422) {
            return this.handleAuthenticationFailure();
          }
        }

        // Handle 401 errors for other endpoints - try to refresh token once
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // Attempt to refresh the token
            const token = await this.refreshTokenIfNeeded();

            if (token && originalRequest.headers) {
              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            return this.handleAuthenticationFailure();
          }
        }

        // Convert axios error to our ApiError format
        const errorMessage =
          error.response?.data &&
          typeof error.response.data === 'object' &&
          'message' in error.response.data
            ? (error.response.data as any).message
            : (error as Error).message || 'An error occurred';

        const apiError: ApiError = {
          message: errorMessage,
          status: error.response?.status || 0,
          data: error.response?.data,
        };

        return Promise.reject(apiError);
      }
    );
  }

  private async handleAuthenticationFailure(): Promise<never> {
    // Clear any stored user data and sign out
    try {
      const authService = await this.getAuthService();
      if (authService) {
        await authService.logout();
      }
    } catch (signOutError) {
      console.error('Error during sign out:', signOutError);
    }

    // Note: Auth store handles localStorage clearing via logout()

    // Redirect to login if not already there
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }

    return Promise.reject(new Error('Authentication failed - redirecting to login'));
  }

  async get<T>(url: string, params?: Record<string, any>): Promise<ApiResponse<T>> {
    const response = await this.client.get(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.client.post(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.client.put(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    const response = await this.client.delete(url);
    return response.data;
  }

  async patch<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response = await this.client.patch(url, data);
    return response.data;
  }
}

// Extend AxiosRequestConfig to include _retry property
declare module 'axios' {
  interface AxiosRequestConfig {
    _retry?: boolean;
  }
}

export const apiClient = new ApiClient();
